import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  DatePicker,
  Button,
  Tag,
  Avatar,
  Card,
  Tooltip,
} from "antd";
import { SearchOutlined, EyeOutlined, EditOutlined } from "@ant-design/icons";
import ViewTableBtn from "../../assets/images/view-icon.svg";
import { useNavigate } from "react-router";
import { ShowToast, Severty } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import userPhoto from "../../assets/images/user.png"; // Default user photo

const { Search } = Input;

const statusColors = {
  Active: "green",
  Inactive: "red",
  Lost: "orange",
  Transferred: "blue",
  Paused: "gold",
};

// Live presence, derived server-side from lastActiveAt. "away" must not share a
// colour with "offline" — they mean different things to whoever reads the list.
const PRESENCE_COLORS = {
  online: "green",
  away: "orange",
  busy: "red",
  offline: "default",
};

// RAG engagement = week-on-week posting-schedule adherence.
// green = 100%, amber = 50–99%, red = <50%, none = no schedule set.
const RAG_STYLES = {
  green: { bg: "#D6F5DD", color: "#00B050", label: "On Track" },
  amber: { bg: "#FFF1D6", color: "#D48806", label: "Needs Attention" },
  red: { bg: "#FBD8D6", color: "#F32013", label: "Off Track" },
  none: { bg: "#EEF0F2", color: "#8A94A6", label: "No Schedule" },
};

// Legend shown inside the hover tooltip so admins can see exactly how the
// colour is derived. The client's own level is bolded at render time.
const RAG_LEGEND = [
  { key: "green", dot: "#00B050", text: "Green — all posts published as per cadence" },
  { key: "amber", dot: "#D48806", text: "Amber — at least 50% of cadence published" },
  { key: "red", dot: "#F32013", text: "Red — under 50% published in the last week" },
];

const renderEngagementRAG = (engagement) => {
  // Backwards-compatible: older payloads sent a boolean.
  const level =
    engagement && typeof engagement === "object"
      ? engagement.level || "none"
      : engagement
      ? "green"
      : "none";
  const s = RAG_STYLES[level] || RAG_STYLES.none;
  const adherence = engagement && typeof engagement === "object" ? engagement.adherence : null;
  const tip =
    level === "none" ? (
      "No posting schedule set for this client yet"
    ) : (
      <div style={{ maxWidth: 250 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          {adherence}% of posting cadence this week
        </div>
        <div style={{ marginBottom: 8, opacity: 0.85 }}>
          {engagement?.actual ?? 0} of {engagement?.expected ?? 0} expected posts published
        </div>
        {RAG_LEGEND.map((r) => (
          <div
            key={r.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
              fontWeight: r.key === level ? 700 : 400,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: r.dot,
                flexShrink: 0,
              }}
            />
            <span>{r.text}</span>
          </div>
        ))}
      </div>
    );
  return (
    <Tooltip title={tip}>
      <span
        style={{
          backgroundColor: s.bg,
          color: s.color,
          padding: "4px 12px",
          borderRadius: "50px",
          fontWeight: 500,
          display: "inline-block",
          whiteSpace: "nowrap",
        }}
      >
        {s.label}
        {level !== "none" && adherence != null ? ` · ${adherence}%` : ""}
      </span>
    </Tooltip>
  );
};

const AccessClients = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    search: "",
    dateRange: null,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { request } = useRequest();

  const columns = [
    {
      title: "CLIENT NAME",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <span
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/view-clients/" + `${record._id}`)}
        >
          <Avatar src={record.image || userPhoto} size="small" style={{ marginRight: 8 }} />
          {text}
        </span>
      ),
    },
    {
      title: "OVERALL ENGAGEMENT",
      dataIndex: "engagement",
      key: "engagement",
      render: (engagement) => renderEngagementRAG(engagement),
    },
    {
      title: "RENEWAL DATE",
      // Latest contract's end date, supplied by the API as `renewalDate`.
      // This column previously read `updated_at` (the user record's
      // last-modified time), which is why it showed "36 minutes ago".
      dataIndex: "renewalDate",
      key: "renewal",
      sorter: (a, b) => new Date(a.renewalDate || 0) - new Date(b.renewalDate || 0),
      render: (renewalDate) => {
        if (!renewalDate) return "-";
        // Contract dates are IST calendar-day boundaries; a fixed +5:30
        // offset keeps the displayed day correct in any viewer timezone.
        const date = moment(renewalDate).utcOffset(330);
        const now = moment().utcOffset(330);
        if (date.isBefore(now, "day")) {
          return (
            <span style={{ color: "red" }}>{date.format("DD MMM YYYY")}</span>
          );
        }
        return <span>{date.format("DD MMM YYYY")}</span>;
      },
    },
    {
      title: "ACTIVE POST",
      dataIndex: "awaitingApproval",
      key: "awaitingApproval",
      render: (text, record) => (
        <div
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => navigate(`/view-clients/${record._id}?tab=posts`)}
        >
          {text}
        </div>
      ),
    },
    {
      title: "PENDING POST",
      dataIndex: "awaitingReject",
      key: "awaitingReject",
      render: (text, record) => (
        <div
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => navigate(`/view-clients/${record._id}?tab=posts`)}
        >
          {text}
        </div>
      ),
    },
    {
      title: "BLOGS IN PROGRESS",
      dataIndex: "blogProgress",
      key: "blogProgress",
      render: (text, record) => (
        <div
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => navigate(`/view-clients/${record._id}?tab=blogs`)}
        >
          {text}
        </div>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "currentStatus",
      key: "currentStatus",
      filters: [
        { text: "Online", value: "online" },
        { text: "Away", value: "away" },
        { text: "Busy", value: "busy" },
        { text: "Offline", value: "offline" },
      ],
      onFilter: (value, record) => (record.currentStatus || "offline") === value,
      // Live presence (online/away/busy/offline), same field driving the
      // status badge on the client profile page and brand-manager's list.
      render: (currentStatus) => (
        <Tag color={PRESENCE_COLORS[currentStatus] || PRESENCE_COLORS.offline}>
          {currentStatus
            ? currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)
            : "Offline"}
        </Tag>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <div className="action-icons">
          <Tooltip title="View Client Profile">
            <Button
              className="table-light-fill-btn"
              onClick={() => navigate("/view-clients/" + `${record._id}`)}
            >
              <img src={ViewTableBtn} alt="View" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const fetchData = (
    page = pagination.current,
    pageSize = pagination.pageSize,
    filters
  ) => {
    let query = `?search=${filter.search}&page=${page}&limit=${pageSize}`;
    if (filter.dateRange && filter.dateRange.length === 2) {
      const [start, end] = filter.dateRange;
      query += `&startDate=${start.format("YYYY-MM-DD")}&endDate=${end.format("YYYY-MM-DD")}`;
    }
    request({
      url: apiPath.getClients + query,
      method: "GET",
      onSuccess: (res) => {
        if (res.status === 200) {
          setData(res?.data?.clients);
          setPagination({
            current: res.data.currentPage,
            pageSize: pageSize,
            total: res.data.totalCount,
          });
        } else {
          ShowToast(res.message || "Failed to fetch profile details", Severty.ERROR);
        }
      },
      onError: () => {
        ShowToast("Failed to fetch profile details", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData(1, pagination.pageSize);
  }, [filter]);

  const handleTableChange = (pag, filters) => {
    setPagination({
      ...pagination,
      current: pag.current,
      pageSize: pag.pageSize,
    });
    fetchData(pag.current, pag.pageSize, filters);
  };

  return (
    <Card
      title="Access Clients"
      bordered={false}
      className="criclebox tablespace mb-24"
      extra={
        <div className="d-flex align-items-end gap-3 flex-wrap">
          <div className="pageHeadingSearch">
            <Input.Search
              placeholder="Search By Name"
              className="searchInput"
              onChange={(e) => {
                setFilter({
                  ...filter,
                  search: e.target.value,
                });
              }}
              allowClear
            />
          </div>
          <div className="role-wrap">
            <DatePicker.RangePicker
              placeholder={["Start Date", "End Date"]}
              value={filter.dateRange}
              onChange={(dates) => {
                setFilter({
                  ...filter,
                  dateRange: dates,
                });
              }}
              allowClear
            />
          </div>
          <div className="d-flex align-items-end gap-3 flex-wrap">
            <Button className="fill-btn" onClick={() => navigate("/brand-access-control")}>
              Brand Access Control
            </Button>
          </div>
        </div>
      }
    >
      <div className="access-clients-page">
        <Table
          columns={columns}
          dataSource={data}
          className="table-responsive"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
          rowKey="_id"
        />
      </div>
    </Card>
  );
};

export default AccessClients;
